import { Link } from "react-router";
import type { ProductProfile } from "../../../shared/goals/profile";
import { Icon } from "../../components/ui/Icon";
import { Rich } from "../../components/ui/Rich";
import type { ProfileView } from "../../lib/productsApi";
import { profileStatus } from "./status";
import { bullets, firstLine } from "./text";
import { Can } from "../../context/AuthContext";

// The prototype's order and names for the sections the learn method writes (its PP).
const PP: [string, string][] = [
  ["what", "What you sell"],
  ["who", "Who it is for"],
  ["offer", "Offer and price"],
  ["next_step", "What you want people to do"],
  ["voice", "How you sound"],
  ["proof", "Proof"],
  ["can_claim", "Safe to say"],
  ["cannot_claim", "Never say"],
];
const PP_IDS = new Set(PP.map(([id]) => id));

function Cell({ title, text }: { title: string; text: string }) {
  return (
    <div className="pp-cell">
      <span>{title}</span>
      <p>{text}</p>
    </div>
  );
}

/** Every section of a confirmed profile: the usual ones in the prototype's order, then any others. */
function wholeProfile(p: ProductProfile): [string, string][] {
  const by = new Map(p.sections.map((s) => [s.id, s.text]));
  return [
    ...PP.filter(([id]) => by.get(id)).map(([id, t]): [string, string] => [t, by.get(id) ?? ""]),
    ...p.sections.filter((s) => !PP_IDS.has(s.id)).map((s): [string, string] => [s.title, s.text]),
  ];
}

const STEP_LINE = {
  learning: ["chip auto", "Learning the business", "The agent is reading its pages now."],
  question: ["chip you", "Waiting for your answer", "It found more than one business with this name."],
  draft: ["chip you", "Waiting for you to confirm", "A draft profile is ready to check."],
} as const;

/** The product page's profile card (the prototype's `profCard`), or where the profile stands when none is confirmed. */
export function ProfileCard({ productId, name, view }: { productId: string; name: string; view: ProfileView }) {
  const { step } = profileStatus(view);
  const flow = `/products/${encodeURIComponent(productId)}/profile`;
  const c = view.confirmed;

  if (!c) {
    const line = step === "none" ? null : STEP_LINE[step];
    return (
      <section className="card pd-sec" id="profile">
        <div className="card-h">
          <h3>{name} profile</h3>
          {line ? (
            <span className={line[0]}>
              {step !== "learning" && <Icon name="user" />}
              {line[1]}
            </span>
          ) : (
            <span className="chip">No profile yet</span>
          )}
        </div>
        <p className="pd-none">{line ? line[2] : "Every goal starts from a product profile you confirm."}</p>
        <Can min="editor">
          <div className="m-act">
            <Link className={line ? "btn sm ghost" : "btn sm"} to={flow}>
              {line ? "Open" : "Learn the business"}
            </Link>
          </div>
        </Can>
      </section>
    );
  }

  const sx = Object.fromEntries(c.profile.sections.map((s) => [s.id, s.text]));
  const cells = PP.filter(([id]) => sx[id] && !["next_step", "can_claim", "cannot_claim"].includes(id));
  const claims = [sx.can_claim && `${bullets(sx.can_claim)} safe to say`, sx.cannot_claim && `${bullets(sx.cannot_claim)} never say`].filter(Boolean).join(" · ");
  return (
    <section className="card pprof" id="profile">
      <div className="card-h">
        <div>
          <h3>{name} profile</h3>
          <div className="sub">Confirmed by you</div>
        </div>
        <Can min="editor">
          {step === "none" ? (
            <Link className="btn sm ghost" to={flow}>
              <Icon name="pen" />
              Edit
            </Link>
          ) : (
            <Link className="chip link" to={flow}>
              {STEP_LINE[step][1]}
              <Icon name="right" />
            </Link>
          )}
        </Can>
      </div>
      {sx.next_step && (
        <div className="pp-next">
          <span>What you want people to do</span>
          <p>{firstLine(sx.next_step)}</p>
        </div>
      )}
      <div className="pp-grid">
        {cells.map(([id, t]) => (
          <Cell key={id} title={t} text={firstLine(sx[id])} />
        ))}
        {claims && <Cell title="What we may say" text={claims} />}
      </div>
      <details className="pp-more">
        <summary>
          See the whole profile
          <Icon name="down" />
        </summary>
        <div className="rows">
          {wholeProfile(c.profile).map(([t, text], k) => (
            <div className="kv" key={k}>
              <span>{t}</span>
              <Rich text={text} />
            </div>
          ))}
        </div>
      </details>
    </section>
  );
}
